import { useState, useMemo } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { useDebounce } from "@/MyCode/hooks/useDebounce"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"
import { Button } from "@/components/ui/button"
import { X } from "lucide-react"
import { PieChart } from "@/components/mortgage/PieChart"

export function RentalPropertyCalculator() {
    // Purchase section
    const [purchasePrice, setPurchasePrice] = useState(200000)
    const [useLoan, setUseLoan] = useState("Yes")
    const [downPayment, setDownPayment] = useState(20)
    const [interestRate, setInterestRate] = useState(6)
    const [loanTerm, setLoanTerm] = useState(30)
    const [closingCost, setClosingCost] = useState(6000)
    const [needRepairs, setNeedRepairs] = useState("Yes")
    const [repairCost, setRepairCost] = useState(20000)
    const [valueAfterRepairs, setValueAfterRepairs] = useState(260000)

    // Income section
    const [monthlyRent, setMonthlyRent] = useState(2000)
    const [monthlyRentAnnualIncrease, setMonthlyRentAnnualIncrease] = useState(3)
    const [otherMonthlyIncome, setOtherMonthlyIncome] = useState(0)
    const [otherMonthlyIncomeAnnualIncrease, setOtherMonthlyIncomeAnnualIncrease] = useState(3)
    const [vacancyRate, setVacancyRate] = useState(5)
    const [managementFee, setManagementFee] = useState(0)

    // Sell section
    const [knowSellPrice, setKnowSellPrice] = useState("No")
    const [valueAppreciation, setValueAppreciation] = useState(3)
    const [holdingLength, setHoldingLength] = useState(10)
    const [costToSell, setCostToSell] = useState(8)

    // Recurring Operating Expenses
    const [propertyTax, setPropertyTax] = useState(3000)
    const [propertyTaxAnnualIncrease, setPropertyTaxAnnualIncrease] = useState(3)
    const [totalInsurance, setTotalInsurance] = useState(1200)
    const [totalInsuranceAnnualIncrease, setTotalInsuranceAnnualIncrease] = useState(3)
    const [hoaFee, setHoaFee] = useState(0)
    const [hoaFeeAnnualIncrease, setHoaFeeAnnualIncrease] = useState(3)
    const [maintenance, setMaintenance] = useState(2000)
    const [maintenanceAnnualIncrease, setMaintenanceAnnualIncrease] = useState(3)
    const [otherCosts, setOtherCosts] = useState(500)
    const [otherCostsAnnualIncrease, setOtherCostsAnnualIncrease] = useState(3)

    // Debounced values
    const debouncedPurchasePrice = useDebounce(purchasePrice, 500)
    const debouncedUseLoan = useDebounce(useLoan, 500)
    const debouncedDownPayment = useDebounce(downPayment, 500)
    const debouncedInterestRate = useDebounce(interestRate, 500)
    const debouncedLoanTerm = useDebounce(loanTerm, 500)
    const debouncedClosingCost = useDebounce(closingCost, 500)
    const debouncedNeedRepairs = useDebounce(needRepairs, 500)
    const debouncedRepairCost = useDebounce(repairCost, 500)
    const debouncedValueAfterRepairs = useDebounce(valueAfterRepairs, 500)
    const debouncedMonthlyRent = useDebounce(monthlyRent, 500)
    const debouncedMonthlyRentAnnualIncrease = useDebounce(monthlyRentAnnualIncrease, 500)
    const debouncedOtherMonthlyIncome = useDebounce(otherMonthlyIncome, 500)
    const debouncedOtherMonthlyIncomeAnnualIncrease = useDebounce(otherMonthlyIncomeAnnualIncrease, 500)
    const debouncedVacancyRate = useDebounce(vacancyRate, 500)
    const debouncedManagementFee = useDebounce(managementFee, 500)
    const debouncedKnowSellPrice = useDebounce(knowSellPrice, 500)
    const debouncedValueAppreciation = useDebounce(valueAppreciation, 500)
    const debouncedHoldingLength = useDebounce(holdingLength, 500)
    const debouncedCostToSell = useDebounce(costToSell, 500)
    const debouncedPropertyTax = useDebounce(propertyTax, 500)
    const debouncedPropertyTaxAnnualIncrease = useDebounce(propertyTaxAnnualIncrease, 500)
    const debouncedTotalInsurance = useDebounce(totalInsurance, 500)
    const debouncedTotalInsuranceAnnualIncrease = useDebounce(totalInsuranceAnnualIncrease, 500)
    const debouncedHoaFee = useDebounce(hoaFee, 500)
    const debouncedHoaFeeAnnualIncrease = useDebounce(hoaFeeAnnualIncrease, 500)
    const debouncedMaintenance = useDebounce(maintenance, 500)
    const debouncedMaintenanceAnnualIncrease = useDebounce(maintenanceAnnualIncrease, 500)
    const debouncedOtherCosts = useDebounce(otherCosts, 500)
    const debouncedOtherCostsAnnualIncrease = useDebounce(otherCostsAnnualIncrease, 500)

    // Calculate loan details
    const loanAmount = useMemo(() => {
        if (debouncedUseLoan === "No") return 0
        return debouncedPurchasePrice * (1 - debouncedDownPayment / 100)
    }, [debouncedPurchasePrice, debouncedDownPayment, debouncedUseLoan])

    const downPaymentAmount = useMemo(() => {
        if (debouncedUseLoan === "No") return debouncedPurchasePrice
        return debouncedPurchasePrice * (debouncedDownPayment / 100)
    }, [debouncedPurchasePrice, debouncedDownPayment, debouncedUseLoan])

    const monthlyLoanPayment = useMemo(() => {
        if (loanAmount <= 0 || debouncedLoanTerm <= 0) return 0
        const monthlyRate = debouncedInterestRate / 100 / 12
        const totalPayments = debouncedLoanTerm * 12

        if (monthlyRate === 0) {
            return loanAmount / totalPayments
        }

        return (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)
    }, [loanAmount, debouncedInterestRate, debouncedLoanTerm])

    // Calculate total initial investment
    const totalInitialInvestment = useMemo(() => {
        let total = downPaymentAmount + debouncedClosingCost
        if (debouncedNeedRepairs === "Yes") {
            total += debouncedRepairCost
        }
        return total
    }, [downPaymentAmount, debouncedClosingCost, debouncedNeedRepairs, debouncedRepairCost])

    // Calculate annual income
    const annualGrossIncome = useMemo(() => {
        const annualRent = debouncedMonthlyRent * 12
        const annualOtherIncome = debouncedOtherMonthlyIncome * 12
        return annualRent + annualOtherIncome
    }, [debouncedMonthlyRent, debouncedOtherMonthlyIncome])

    // Calculate vacancy loss
    const annualVacancyLoss = useMemo(() => {
        return annualGrossIncome * (debouncedVacancyRate / 100)
    }, [annualGrossIncome, debouncedVacancyRate])

    // Calculate management fee
    const annualManagementFee = useMemo(() => {
        return annualGrossIncome * (debouncedManagementFee / 100)
    }, [annualGrossIncome, debouncedManagementFee])

    // Calculate annual operating expenses
    const annualOperatingExpenses = useMemo(() => {
        return debouncedPropertyTax +
            debouncedTotalInsurance +
            debouncedHoaFee +
            debouncedMaintenance +
            debouncedOtherCosts
    }, [debouncedPropertyTax, debouncedTotalInsurance, debouncedHoaFee, debouncedMaintenance, debouncedOtherCosts])

    // Calculate annual debt service (loan payments)
    const annualDebtService = useMemo(() => {
        return monthlyLoanPayment * 12
    }, [monthlyLoanPayment])

    // Calculate net operating income (NOI)
    const netOperatingIncome = useMemo(() => {
        return annualGrossIncome - annualVacancyLoss - annualManagementFee - annualOperatingExpenses
    }, [annualGrossIncome, annualVacancyLoss, annualManagementFee, annualOperatingExpenses])

    // Calculate cash flow
    const annualCashFlow = useMemo(() => {
        return netOperatingIncome - annualDebtService
    }, [netOperatingIncome, annualDebtService])

    const monthlyCashFlow = useMemo(() => {
        return annualCashFlow / 12
    }, [annualCashFlow])

    // Calculate cap rate
    const capRate = useMemo(() => {
        if (debouncedValueAfterRepairs <= 0) return 0
        return (netOperatingIncome / debouncedValueAfterRepairs) * 100
    }, [netOperatingIncome, debouncedValueAfterRepairs])

    // Calculate cash on cash return
    const cashOnCashReturn = useMemo(() => {
        if (totalInitialInvestment <= 0) return 0
        return (annualCashFlow / totalInitialInvestment) * 100
    }, [annualCashFlow, totalInitialInvestment])

    // Calculate ROI over holding period
    const futurePropertyValue = useMemo(() => {
        if (debouncedKnowSellPrice === "Yes") {
            // User knows sell price - would need another field, for now use appreciation
            return debouncedValueAfterRepairs * Math.pow(1 + debouncedValueAppreciation / 100, debouncedHoldingLength)
        } else {
            return debouncedValueAfterRepairs * Math.pow(1 + debouncedValueAppreciation / 100, debouncedHoldingLength)
        }
    }, [debouncedKnowSellPrice, debouncedValueAfterRepairs, debouncedValueAppreciation, debouncedHoldingLength])

    const totalCashFlowOverHoldingPeriod = useMemo(() => {
        // Simplified: assumes constant cash flow (doesn't account for increases)
        return annualCashFlow * debouncedHoldingLength
    }, [annualCashFlow, debouncedHoldingLength])

    const remainingLoanBalance = useMemo(() => {
        if (loanAmount <= 0 || debouncedLoanTerm <= 0) return 0
        const monthlyRate = debouncedInterestRate / 100 / 12
        const totalPayments = debouncedLoanTerm * 12
        const paymentsMade = debouncedHoldingLength * 12

        if (paymentsMade >= totalPayments) return 0

        if (monthlyRate === 0) {
            return loanAmount - (monthlyLoanPayment * paymentsMade)
        }

        return loanAmount * (Math.pow(1 + monthlyRate, totalPayments) - Math.pow(1 + monthlyRate, paymentsMade)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)
    }, [loanAmount, debouncedInterestRate, debouncedLoanTerm, debouncedHoldingLength, monthlyLoanPayment])

    const netSaleProceeds = useMemo(() => {
        const saleCost = futurePropertyValue * (debouncedCostToSell / 100)
        return futurePropertyValue - saleCost - remainingLoanBalance
    }, [futurePropertyValue, debouncedCostToSell, remainingLoanBalance])

    const totalROI = useMemo(() => {
        if (totalInitialInvestment <= 0) return 0
        const totalReturn = totalCashFlowOverHoldingPeriod + netSaleProceeds
        return ((totalReturn - totalInitialInvestment) / totalInitialInvestment) * 100
    }, [totalCashFlowOverHoldingPeriod, netSaleProceeds, totalInitialInvestment])

    const annualizedROI = useMemo(() => {
        if (debouncedHoldingLength <= 0) return 0
        return totalROI / debouncedHoldingLength
    }, [totalROI, debouncedHoldingLength])

    // Calculate IRR (Internal Rate of Return) using Newton-Raphson method
    const calculateIRR = (cashFlows: number[]): number => {
        if (cashFlows.length === 0 || cashFlows[0] >= 0) return 0

        let rate = 0.1 // Initial guess
        const tolerance = 0.0001
        const maxIterations = 100

        for (let i = 0; i < maxIterations; i++) {
            let npv = 0
            let npvDerivative = 0

            for (let year = 0; year < cashFlows.length; year++) {
                const discountFactor = Math.pow(1 + rate, year)
                npv += cashFlows[year] / discountFactor
                npvDerivative -= (year * cashFlows[year]) / (discountFactor * (1 + rate))
            }

            if (Math.abs(npv) < tolerance) break

            const newRate = rate - npv / npvDerivative
            if (isNaN(newRate) || !isFinite(newRate)) break
            rate = newRate

            if (rate < -0.99) rate = -0.99
            if (rate > 10) rate = 10
        }

        return rate * 100
    }

    // Calculate year-by-year breakdown
    interface YearData {
        year: number
        annualIncome: number
        mortgage: number
        expenses: number
        cashFlow: number
        cashOnCashReturn: number
        equityAccumulated: number
        propertyValue: number
        remainingLoanBalance: number
        cashToReceive: number
        irr: number
    }

    const yearByYearData = useMemo(() => {
        const data: YearData[] = []
        const maxYears = Math.max(20, debouncedHoldingLength)

        let currentRent = debouncedMonthlyRent
        let currentOtherIncome = debouncedOtherMonthlyIncome
        let currentPropertyTax = debouncedPropertyTax
        let currentInsurance = debouncedTotalInsurance
        let currentHoaFee = debouncedHoaFee
        let currentMaintenance = debouncedMaintenance
        let currentOtherCosts = debouncedOtherCosts
        let currentPropertyValue = debouncedValueAfterRepairs
        let currentLoanBalance = loanAmount
        let totalEquity = downPaymentAmount

        const monthlyRate = debouncedInterestRate / 100 / 12
        const totalPayments = debouncedLoanTerm * 12

        // Initial cash flow (negative for initial investment)
        const initialCashFlow = -totalInitialInvestment
        const cashFlowsForIRR = [initialCashFlow]

        for (let year = 1; year <= maxYears; year++) {
            // Calculate annual income with increases
            const annualRent = currentRent * 12
            const annualOtherIncome = currentOtherIncome * 12
            const annualIncome = annualRent + annualOtherIncome

            // Calculate expenses with increases
            const vacancyLoss = annualIncome * (debouncedVacancyRate / 100)
            const managementFee = annualIncome * (debouncedManagementFee / 100)
            const annualExpenses = currentPropertyTax + currentInsurance + currentHoaFee +
                currentMaintenance + currentOtherCosts + vacancyLoss + managementFee

            // Calculate mortgage payment
            const annualMortgage = monthlyLoanPayment * 12

            // Calculate cash flow
            const annualCashFlow = annualIncome - annualExpenses - annualMortgage

            // Calculate cash on cash return
            const cashOnCashReturn = totalInitialInvestment > 0
                ? (annualCashFlow / totalInitialInvestment) * 100
                : 0

            // Calculate remaining loan balance
            const paymentsMade = year * 12
            if (paymentsMade < totalPayments && currentLoanBalance > 0) {
                if (monthlyRate === 0) {
                    currentLoanBalance = Math.max(0, currentLoanBalance - annualMortgage)
                } else {
                    currentLoanBalance = loanAmount *
                        (Math.pow(1 + monthlyRate, totalPayments) - Math.pow(1 + monthlyRate, paymentsMade)) /
                        (Math.pow(1 + monthlyRate, totalPayments) - 1)
                }
            } else {
                currentLoanBalance = 0
            }

            // Calculate equity accumulated
            const principalPaid = loanAmount - currentLoanBalance
            totalEquity = downPaymentAmount + principalPaid

            // Calculate property value with appreciation
            currentPropertyValue = debouncedValueAfterRepairs *
                Math.pow(1 + debouncedValueAppreciation / 100, year)

            // Calculate cash to receive if sold
            const saleCost = currentPropertyValue * (debouncedCostToSell / 100)
            const cashToReceive = currentPropertyValue - saleCost - currentLoanBalance

            // Calculate IRR up to this year
            cashFlowsForIRR.push(annualCashFlow)
            const irr = year === 1 ? 0 : calculateIRR([...cashFlowsForIRR, cashToReceive])

            data.push({
                year,
                annualIncome,
                mortgage: annualMortgage,
                expenses: annualExpenses,
                cashFlow: annualCashFlow,
                cashOnCashReturn,
                equityAccumulated: totalEquity,
                propertyValue: currentPropertyValue,
                remainingLoanBalance: currentLoanBalance,
                cashToReceive,
                irr
            })

            // Apply annual increases for next year
            currentRent *= (1 + debouncedMonthlyRentAnnualIncrease / 100)
            currentOtherIncome *= (1 + debouncedOtherMonthlyIncomeAnnualIncrease / 100)
            currentPropertyTax *= (1 + debouncedPropertyTaxAnnualIncrease / 100)
            currentInsurance *= (1 + debouncedTotalInsuranceAnnualIncrease / 100)
            currentHoaFee *= (1 + debouncedHoaFeeAnnualIncrease / 100)
            currentMaintenance *= (1 + debouncedMaintenanceAnnualIncrease / 100)
            currentOtherCosts *= (1 + debouncedOtherCostsAnnualIncrease / 100)
        }

        return data
    }, [
        debouncedMonthlyRent, debouncedOtherMonthlyIncome, debouncedMonthlyRentAnnualIncrease,
        debouncedOtherMonthlyIncomeAnnualIncrease, debouncedVacancyRate, debouncedManagementFee,
        debouncedPropertyTax, debouncedTotalInsurance, debouncedHoaFee, debouncedMaintenance,
        debouncedOtherCosts, debouncedPropertyTaxAnnualIncrease, debouncedTotalInsuranceAnnualIncrease,
        debouncedHoaFeeAnnualIncrease, debouncedMaintenanceAnnualIncrease, debouncedOtherCostsAnnualIncrease,
        monthlyLoanPayment, loanAmount, debouncedInterestRate, debouncedLoanTerm,
        downPaymentAmount, totalInitialInvestment, debouncedValueAfterRepairs,
        debouncedValueAppreciation, debouncedCostToSell, debouncedHoldingLength
    ])

    // Calculate summary for specific years
    const calculateSummaryForYears = (years: number) => {
        if (years <= 0 || years > yearByYearData.length) return null

        const data = yearByYearData.slice(0, years)
        const totalIncome = data.reduce((sum, d) => sum + d.annualIncome, 0)
        const totalExpenses = data.reduce((sum, d) => sum + d.expenses, 0)
        const totalMortgage = data.reduce((sum, d) => sum + d.mortgage, 0)
        const totalCashFlow = data.reduce((sum, d) => sum + d.cashFlow, 0)
        const finalYearData = data[data.length - 1]

        const totalCashOnCashReturn = totalInitialInvestment > 0
            ? (totalCashFlow / totalInitialInvestment) * 100
            : 0

        const totalProfitWhenSold = totalCashFlow + finalYearData.cashToReceive - totalInitialInvestment
        const purchaseCapRate = debouncedValueAfterRepairs > 0
            ? ((totalIncome - totalExpenses) / years / debouncedValueAfterRepairs) * 100
            : 0

        return {
            years,
            returnIRR: finalYearData.irr,
            totalProfitWhenSold,
            totalCashOnCashReturn,
            purchaseCapRate,
            totalRentalIncome: totalIncome,
            totalExpenses: totalExpenses + totalMortgage,
            totalNetOperatingIncome: totalIncome - totalExpenses
        }
    }

    // First year data
    const firstYearData = useMemo(() => {
        if (yearByYearData.length === 0) return null
        return yearByYearData[0]
    }, [yearByYearData])

    // First year expense breakdown for pie chart
    const firstYearExpenseBreakdown = useMemo(() => {
        if (!firstYearData) return []

        const vacancyLoss = debouncedMonthlyRent * 12 * (debouncedVacancyRate / 100) +
            debouncedOtherMonthlyIncome * 12 * (debouncedVacancyRate / 100)
        const managementFee = (debouncedMonthlyRent * 12 + debouncedOtherMonthlyIncome * 12) *
            (debouncedManagementFee / 100)

        return [
            { label: "Vacancy", value: vacancyLoss, color: "#3b82f6" },
            { label: "Property Tax", value: debouncedPropertyTax, color: "#10b981" },
            { label: "Total Insurance", value: debouncedTotalInsurance, color: "#ef4444" },
            { label: "Maintenance Cost", value: debouncedMaintenance, color: "#06b6d4" },
            { label: "Other Cost", value: debouncedOtherCosts, color: "#a855f7" },
            ...(managementFee > 0 ? [{ label: "Management Fee", value: managementFee, color: "#f59e0b" }] : [])
        ].filter(item => item.value > 0)
    }, [firstYearData, debouncedMonthlyRent, debouncedOtherMonthlyIncome, debouncedVacancyRate,
        debouncedManagementFee, debouncedPropertyTax, debouncedTotalInsurance, debouncedMaintenance, debouncedOtherCosts])

    // Helper function to parse numeric value from string that may contain units
    const parseNumericValue = (value: any): number => {
        if (value === undefined || value === null || value === '') return 0
        if (typeof value === 'number') return value
        if (typeof value === 'string') {
            // Remove non-numeric characters except decimal point and minus sign
            const numericStr = value.replace(/[^0-9.-]/g, '')
            const parsed = parseFloat(numericStr)
            return isNaN(parsed) ? 0 : parsed
        }
        return 0
    }

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.purchasePrice !== undefined) setPurchasePrice(parseNumericValue(data.purchasePrice))
        if (data.useLoan !== undefined) setUseLoan(data.useLoan)
        if (data.downPayment !== undefined) setDownPayment(parseNumericValue(data.downPayment))
        if (data.interestRate !== undefined) setInterestRate(parseNumericValue(data.interestRate))
        if (data.loanTerm !== undefined) setLoanTerm(parseNumericValue(data.loanTerm))
        if (data.closingCost !== undefined) setClosingCost(parseNumericValue(data.closingCost))
        if (data.needRepairs !== undefined) setNeedRepairs(data.needRepairs)
        if (data.repairCost !== undefined) setRepairCost(parseNumericValue(data.repairCost))
        if (data.valueAfterRepairs !== undefined) setValueAfterRepairs(parseNumericValue(data.valueAfterRepairs))
        if (data.monthlyRent !== undefined) setMonthlyRent(parseNumericValue(data.monthlyRent))
        if (data.monthlyRentAnnualIncrease !== undefined) setMonthlyRentAnnualIncrease(parseNumericValue(data.monthlyRentAnnualIncrease))
        if (data.otherMonthlyIncome !== undefined) setOtherMonthlyIncome(parseNumericValue(data.otherMonthlyIncome))
        if (data.otherMonthlyIncomeAnnualIncrease !== undefined) setOtherMonthlyIncomeAnnualIncrease(parseNumericValue(data.otherMonthlyIncomeAnnualIncrease))
        if (data.vacancyRate !== undefined) setVacancyRate(parseNumericValue(data.vacancyRate))
        if (data.managementFee !== undefined) setManagementFee(parseNumericValue(data.managementFee))
        if (data.knowSellPrice !== undefined) setKnowSellPrice(data.knowSellPrice)
        if (data.valueAppreciation !== undefined) setValueAppreciation(parseNumericValue(data.valueAppreciation))
        if (data.holdingLength !== undefined) setHoldingLength(parseNumericValue(data.holdingLength))
        if (data.costToSell !== undefined) setCostToSell(parseNumericValue(data.costToSell))
        if (data.propertyTax !== undefined) setPropertyTax(parseNumericValue(data.propertyTax))
        if (data.propertyTaxAnnualIncrease !== undefined) setPropertyTaxAnnualIncrease(parseNumericValue(data.propertyTaxAnnualIncrease))
        if (data.totalInsurance !== undefined) setTotalInsurance(parseNumericValue(data.totalInsurance))
        if (data.totalInsuranceAnnualIncrease !== undefined) setTotalInsuranceAnnualIncrease(parseNumericValue(data.totalInsuranceAnnualIncrease))
        if (data.hoaFee !== undefined) setHoaFee(parseNumericValue(data.hoaFee))
        if (data.hoaFeeAnnualIncrease !== undefined) setHoaFeeAnnualIncrease(parseNumericValue(data.hoaFeeAnnualIncrease))
        if (data.maintenance !== undefined) setMaintenance(parseNumericValue(data.maintenance))
        if (data.maintenanceAnnualIncrease !== undefined) setMaintenanceAnnualIncrease(parseNumericValue(data.maintenanceAnnualIncrease))
        if (data.otherCosts !== undefined) setOtherCosts(parseNumericValue(data.otherCosts))
        if (data.otherCostsAnnualIncrease !== undefined) setOtherCostsAnnualIncrease(parseNumericValue(data.otherCostsAnnualIncrease))
    }

    const handleClear = () => {
        setPurchasePrice(200000)
        setUseLoan("Yes")
        setDownPayment(20)
        setInterestRate(6)
        setLoanTerm(30)
        setClosingCost(6000)
        setNeedRepairs("Yes")
        setRepairCost(20000)
        setValueAfterRepairs(260000)
        setMonthlyRent(2000)
        setMonthlyRentAnnualIncrease(3)
        setOtherMonthlyIncome(0)
        setOtherMonthlyIncomeAnnualIncrease(3)
        setVacancyRate(5)
        setManagementFee(0)
        setKnowSellPrice("No")
        setValueAppreciation(3)
        setHoldingLength(10)
        setCostToSell(8)
        setPropertyTax(3000)
        setPropertyTaxAnnualIncrease(3)
        setTotalInsurance(1200)
        setTotalInsuranceAnnualIncrease(3)
        setHoaFee(0)
        setHoaFeeAnnualIncrease(3)
        setMaintenance(2000)
        setMaintenanceAnnualIncrease(3)
        setOtherCosts(500)
        setOtherCostsAnnualIncrease(3)
    }

    const steps: Step[] = [
        {
            id: "rental-property",
            title: "Rental Property Calculator",
            description: "Calculate ROI and cash flow for rental properties",
            fields: [
                // Purchase Section - Left Column
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "purchasePrice",
                    label: "Purchase Price",
                    placeholder: "200000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        purchasePrice: 'required|numeric|min:0'
                    }
                },

            ]
        },
        {
            id: "useload",
            title: "Use Loan?",
            description: "Are you taking out a loan to purchase the property?",
            fields: [

                {
                    type: "radio",
                    name: "useLoan",
                    label: "Use Loan?",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: [
                            { label: "Yes", value: "Yes" },
                            { label: "No", value: "No" }
                        ]
                    },
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        useLoan: 'required'
                    }
                },

                {
                    type: "inputWithEndSelect",
                    name: "downPayment",
                    label: "Down Payment",
                    placeholder: "20",
                    required: true,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    visibleWhen: [
                        { field: "useLoan", op: "=" as const, value: "Yes" }
                    ],
                    laravelValidationRules: {
                        downPayment: 'required|numeric|min:0|max:100'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "interestRate",
                    label: "Interest Rate",
                    placeholder: "6",
                    required: true,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    visibleWhen: [
                        { field: "useLoan", op: "=" as const, value: "Yes" }
                    ],
                    laravelValidationRules: {
                        interestRate: 'required|numeric|min:0|max:100'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "loanTerm",
                    label: "Loan Term",
                    placeholder: "30",
                    required: true,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "years", value: "years" }
                        ]
                    },
                    visibleWhen: [
                        { field: "useLoan", op: "=" as const, value: "Yes" }
                    ],
                    laravelValidationRules: {
                        loanTerm: 'required|numeric|min:1|max:50'
                    }
                },

                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "closingCost",
                    label: "Closing Cost",
                    placeholder: "6000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        closingCost: 'required|numeric|min:0'
                    }
                },
                {
                    type: "radio",
                    name: "needRepairs",
                    label: "Need Repairs?",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: [
                            { label: "Yes", value: "Yes" },
                            { label: "No", value: "No" }
                        ]
                    },
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        needRepairs: 'required'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "repairCost",
                    label: "Repair Cost",
                    placeholder: "20000",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 12 },
                    visibleWhen: [
                        { field: "needRepairs", op: "=" as const, value: "Yes" }
                    ],
                    laravelValidationRules: {
                        repairCost: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "valueAfterRepairs",
                    label: "Value after Repairs",
                    placeholder: "260000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        valueAfterRepairs: 'required|numeric|min:0'
                    }
                },
            ]
        }, {
            id: "rental-income",
            title: "Rental Income",
            description: "Enter your rental income information",
            fields: [
                // Income Section - Right Column
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "monthlyRent",
                    label: "Monthly Rent",
                    placeholder: "2000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        monthlyRent: 'required|numeric|min:0'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "monthlyRentAnnualIncrease",
                    label: "Annual Increase",
                    placeholder: "3",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        monthlyRentAnnualIncrease: 'nullable|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "otherMonthlyIncome",
                    label: "Other Monthly Income",
                    placeholder: "0",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        otherMonthlyIncome: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "otherMonthlyIncomeAnnualIncrease",
                    label: "Annual Increase",
                    placeholder: "3",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        otherMonthlyIncomeAnnualIncrease: 'nullable|numeric|min:0|max:100'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "vacancyRate",
                    label: "Vacancy Rate",
                    placeholder: "5",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        vacancyRate: 'nullable|numeric|min:0|max:100'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "managementFee",
                    label: "Management Fee",
                    placeholder: "0",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        managementFee: 'nullable|numeric|min:0|max:100'
                    }
                },
            ]
        },
        {
            id: "rental-sell",
            title: "Rental Sell",
            description: "Enter your rental sell information",
            fields: [
                // Sell Section
                // {
                //     type: "radio",
                //     name: "knowSellPrice",
                //     label: "Do You Know the Sell Price?",
                //     required: true,
                //     options: {
                //         source: "static" as const,
                //         items: [
                //             { label: "Yes", value: "Yes" },
                //             { label: "No", value: "No" }
                //         ]
                //     },
                //     ui: { colSpan: 12 },
                //     laravelValidationRules: {
                //         knowSellPrice: 'required'
                //     }
                // },
                {
                    type: "inputWithEndSelect",
                    name: "valueAppreciation",
                    label: "Value Appreciation",
                    placeholder: "3",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "% per year", value: "% per year" }
                        ]
                    },
                    laravelValidationRules: {
                        valueAppreciation: 'nullable|numeric|min:0|max:100'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "holdingLength",
                    label: "Holding Length",
                    placeholder: "10",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "years", value: "years" }
                        ]
                    },
                    laravelValidationRules: {
                        holdingLength: 'nullable|numeric|min:1|max:100'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "costToSell",
                    label: "Cost to Sell",
                    placeholder: "8",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        costToSell: 'nullable|numeric|min:0|max:100'
                    }
                },
            ]
        },
        {
            id: "rental-operating-expenses",
            title: "Rental Operating Expenses",
            description: "Enter your rental operating expenses information",
            fields: [
                // Recurring Operating Expenses
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "propertyTax",
                    label: "Property Tax",
                    placeholder: "3000",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        propertyTax: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "propertyTaxAnnualIncrease",
                    label: "Annual Increase",
                    placeholder: "3",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        propertyTaxAnnualIncrease: 'nullable|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "totalInsurance",
                    label: "Total Insurance",
                    placeholder: "1200",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        totalInsurance: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "totalInsuranceAnnualIncrease",
                    label: "Annual Increase",
                    placeholder: "3",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        totalInsuranceAnnualIncrease: 'nullable|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "hoaFee",
                    label: "HOA Fee",
                    placeholder: "0",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        hoaFee: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "hoaFeeAnnualIncrease",
                    label: "Annual Increase",
                    placeholder: "3",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        hoaFeeAnnualIncrease: 'nullable|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "maintenance",
                    label: "Maintenance",
                    placeholder: "2000",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        maintenance: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "maintenanceAnnualIncrease",
                    label: "Annual Increase",
                    placeholder: "3",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        maintenanceAnnualIncrease: 'nullable|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "otherCosts",
                    label: "Other Costs",
                    placeholder: "500",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 500],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        otherCosts: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "inputWithEndSelect",
                    name: "otherCostsAnnualIncrease",
                    label: "Annual Increase",
                    placeholder: "3",
                    required: false,
                    inputType: "number",
                    ui: {
                        colSpan: 12,
                        endSelectOptions: [
                            { label: "%", value: "%" }
                        ]
                    },
                    laravelValidationRules: {
                        otherCostsAnnualIncrease: 'nullable|numeric|min:0|max:100'
                    }
                },
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Rental Property Calculator</h1>
                    <p className="text-muted-foreground">
                        Calculate ROI, cash flow, and investment returns for rental properties
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={handleClear} variant="outline" className="gap-2">
                        <X className="h-4 w-4" />
                        Clear
                    </Button>
                </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            purchasePrice: purchasePrice,
                            useLoan: useLoan,
                            downPayment: String(downPayment) + "%",
                            interestRate: String(interestRate) + "%",
                            loanTerm: String(loanTerm) + "years",
                            closingCost: closingCost,
                            needRepairs: needRepairs,
                            repairCost: repairCost,
                            valueAfterRepairs: valueAfterRepairs,
                            monthlyRent: monthlyRent,
                            monthlyRentAnnualIncrease: String(monthlyRentAnnualIncrease) + "%",
                            otherMonthlyIncome: otherMonthlyIncome,
                            otherMonthlyIncomeAnnualIncrease: String(otherMonthlyIncomeAnnualIncrease) + "%",
                            vacancyRate: String(vacancyRate) + "%",
                            managementFee: String(managementFee) + "%",
                            knowSellPrice: knowSellPrice,
                            valueAppreciation: String(valueAppreciation) + "% per year",
                            holdingLength: String(holdingLength) + "years",
                            costToSell: String(costToSell) + "%",
                            propertyTax: propertyTax,
                            propertyTaxAnnualIncrease: String(propertyTaxAnnualIncrease) + "%",
                            totalInsurance: totalInsurance,
                            totalInsuranceAnnualIncrease: String(totalInsuranceAnnualIncrease) + "%",
                            hoaFee: hoaFee,
                            hoaFeeAnnualIncrease: String(hoaFeeAnnualIncrease) + "%",
                            maintenance: maintenance,
                            maintenanceAnnualIncrease: String(maintenanceAnnualIncrease) + "%",
                            otherCosts: otherCosts,
                            otherCostsAnnualIncrease: String(otherCostsAnnualIncrease) + "%",
                        }}
                        resetOnInitialValuesChange={false}
                    />
                </motion.div>

                {/* Right Side - Results */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-6"
                >
                    {/* First Year Expense Breakdown Pie Chart */}
                    {firstYearExpenseBreakdown.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.3 }}
                            className="bg-card border border-border rounded-lg p-6"
                        >
                            <h2 className="text-2xl font-bold mb-4">First Year Expense Breakdown</h2>
                            <div className="flex justify-center">
                                <PieChart data={firstYearExpenseBreakdown} />
                            </div>
                        </motion.div>
                    )}
                    {/* Summary Card */}
                    <div className="bg-card border-2 border-primary/20 rounded-lg p-6">
                        <h2 className="text-xl font-semibold mb-4">Investment Summary</h2>
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Total Initial Investment</p>
                                <p className="text-2xl font-bold">
                                    $<AnimatedNumber value={totalInitialInvestment} decimals={0} />
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Monthly Cash Flow</p>
                                <p className={`text-2xl font-bold ${monthlyCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    $<AnimatedNumber value={monthlyCashFlow} decimals={2} />
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Annual Cash Flow</p>
                                <p className={`text-xl font-bold ${annualCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    $<AnimatedNumber value={annualCashFlow} decimals={2} />
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Cash on Cash Return</p>
                                <p className={`text-xl font-bold ${cashOnCashReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    <AnimatedNumber value={cashOnCashReturn} decimals={2} />%
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Cap Rate</p>
                                <p className="text-xl font-bold">
                                    <AnimatedNumber value={capRate} decimals={2} />%
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Income & Expenses */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">Income & Expenses</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Annual Gross Income:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={annualGrossIncome} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Vacancy Loss:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={annualVacancyLoss} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Management Fee:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={annualManagementFee} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Operating Expenses:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={annualOperatingExpenses} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Debt Service:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={annualDebtService} decimals={2} />
                                </span>
                            </div>
                            <div className="pt-3 border-t border-border flex justify-between">
                                <span className="text-base font-semibold">Net Operating Income:</span>
                                <span className="text-base font-bold text-primary">
                                    $<AnimatedNumber value={netOperatingIncome} decimals={2} />
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ROI Projection */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">ROI Projection ({debouncedHoldingLength} years)</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Total Cash Flow:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={totalCashFlowOverHoldingPeriod} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Future Property Value:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={futurePropertyValue} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Remaining Loan Balance:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={remainingLoanBalance} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Net Sale Proceeds:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={netSaleProceeds} decimals={2} />
                                </span>
                            </div>
                            <div className="pt-3 border-t border-border flex justify-between">
                                <span className="text-base font-semibold">Total ROI:</span>
                                <span className={`text-base font-bold ${totalROI >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    <AnimatedNumber value={totalROI} decimals={2} />%
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Annualized ROI:</span>
                                <span className={`text-sm font-semibold ${annualizedROI >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    <AnimatedNumber value={annualizedROI} decimals={2} />%
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* First Year Income and Expense */}
            {firstYearData && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                    className="bg-card border border-border rounded-lg p-6"
                >
                    <h2 className="text-2xl font-bold mb-4">First Year Income and Expense</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-primary/10 border-b border-border">
                                    <th className="text-left p-3 font-semibold">Item</th>
                                    <th className="text-right p-3 font-semibold">Monthly</th>
                                    <th className="text-right p-3 font-semibold">Annual</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b border-border">
                                    <td className="p-3 text-muted-foreground">Income</td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={(debouncedMonthlyRent + debouncedOtherMonthlyIncome)} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={firstYearData.annualIncome} decimals={2} />
                                    </td>
                                </tr>
                                <tr className="border-b border-border">
                                    <td className="p-3 text-muted-foreground">Vacancy ({debouncedVacancyRate}%)</td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={(debouncedMonthlyRent + debouncedOtherMonthlyIncome) * (debouncedVacancyRate / 100)} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={firstYearData.annualIncome * (debouncedVacancyRate / 100)} decimals={2} />
                                    </td>
                                </tr>
                                <tr className="border-b border-border">
                                    <td className="p-3 text-muted-foreground">Property Tax</td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={debouncedPropertyTax / 12} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={debouncedPropertyTax} decimals={2} />
                                    </td>
                                </tr>
                                <tr className="border-b border-border">
                                    <td className="p-3 text-muted-foreground">Total Insurance</td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={debouncedTotalInsurance / 12} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={debouncedTotalInsurance} decimals={2} />
                                    </td>
                                </tr>
                                <tr className="border-b border-border">
                                    <td className="p-3 text-muted-foreground">Maintenance Cost</td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={debouncedMaintenance / 12} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={debouncedMaintenance} decimals={2} />
                                    </td>
                                </tr>
                                <tr className="border-b border-border">
                                    <td className="p-3 text-muted-foreground">Other Cost</td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={debouncedOtherCosts / 12} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right font-semibold">
                                        $<AnimatedNumber value={debouncedOtherCosts} decimals={2} />
                                    </td>
                                </tr>
                                <tr className="border-b-2 border-border">
                                    <td className="p-3 font-semibold">Cash Flow</td>
                                    <td className="p-3 text-right font-bold">
                                        $<AnimatedNumber value={firstYearData.cashFlow / 12} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right font-bold">
                                        $<AnimatedNumber value={firstYearData.cashFlow} decimals={2} />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-3 font-semibold">Net Operating Income (NOI)</td>
                                    <td className="p-3 text-right font-bold">
                                        $<AnimatedNumber value={(firstYearData.annualIncome - firstYearData.expenses) / 12} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right font-bold">
                                        $<AnimatedNumber value={firstYearData.annualIncome - firstYearData.expenses} decimals={2} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            )}

            {/* Summary Tables for 1, 5, 10, 20 years */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 5, 10, 20].map((years) => {
                    const summary = calculateSummaryForYears(years)
                    if (!summary) return null

                    return (
                        <motion.div
                            key={years}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 + years * 0.05, duration: 0.3 }}
                            className="bg-card border border-border rounded-lg p-6"
                        >
                            <h2 className="text-xl font-bold mb-4">For the {years} {years === 1 ? 'Year' : 'Years'} Invested</h2>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Return (IRR):</span>
                                    <span className={`text-sm font-semibold ${summary.returnIRR >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        <AnimatedNumber value={summary.returnIRR} decimals={2} />% per year
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Total Profit when Sold:</span>
                                    <span className={`text-sm font-semibold ${summary.totalProfitWhenSold >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        $<AnimatedNumber value={summary.totalProfitWhenSold} decimals={2} />
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Cash on Cash Return:</span>
                                    <span className={`text-sm font-semibold ${summary.totalCashOnCashReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        <AnimatedNumber value={summary.totalCashOnCashReturn} decimals={2} />%
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Purchase Capitalization Rate:</span>
                                    <span className="text-sm font-semibold text-green-600">
                                        <AnimatedNumber value={summary.purchaseCapRate} decimals={2} />%
                                    </span>
                                </div>
                                <div className="pt-3 border-t border-border space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Total Rental Income:</span>
                                        <span className="text-sm font-semibold">
                                            $<AnimatedNumber value={summary.totalRentalIncome} decimals={2} />
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Total Expenses:</span>
                                        <span className="text-sm font-semibold">
                                            $<AnimatedNumber value={summary.totalExpenses} decimals={2} />
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Total Net Operating Income:</span>
                                        <span className="text-sm font-semibold">
                                            $<AnimatedNumber value={summary.totalNetOperatingIncome} decimals={2} />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )
                })}
            </div>



            {/* Breakdown Over Time */}
            {yearByYearData.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.3 }}
                    className="bg-card border border-border rounded-lg p-6 overflow-x-auto"
                >
                    <h2 className="text-2xl font-bold mb-4">Breakdown Over Time</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse min-w-[1000px]">
                            <thead>
                                <tr className="bg-primary/10 border-b border-border">
                                    <th className="text-left p-3 font-semibold">Year</th>
                                    <th className="text-right p-3 font-semibold">Annual Income</th>
                                    <th className="text-right p-3 font-semibold">Mortgage</th>
                                    <th className="text-right p-3 font-semibold">Expenses</th>
                                    <th className="text-right p-3 font-semibold">Cash Flow</th>
                                    <th className="text-right p-3 font-semibold">Cash on Cash Return</th>
                                    <th className="text-right p-3 font-semibold">Equity Accumulated</th>
                                    <th className="text-right p-3 font-semibold">If Sold at Year End</th>
                                    <th className="text-right p-3 font-semibold">Return (IRR)</th>
                                </tr>
                                <tr className="bg-primary/5 border-b border-border">
                                    <th className="text-left p-3 font-semibold"></th>
                                    <th className="text-right p-3 font-semibold"></th>
                                    <th className="text-right p-3 font-semibold"></th>
                                    <th className="text-right p-3 font-semibold"></th>
                                    <th className="text-right p-3 font-semibold"></th>
                                    <th className="text-right p-3 font-semibold"></th>
                                    <th className="text-right p-3 font-semibold"></th>
                                    <th className="text-right p-3 font-semibold">Cash to Receive</th>
                                    <th className="text-right p-3 font-semibold"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b border-border bg-muted/30">
                                    <td className="p-3 font-semibold">Begin</td>
                                    <td className="p-3 text-right">-</td>
                                    <td className="p-3 text-right">-</td>
                                    <td className="p-3 text-right">-</td>
                                    <td className="p-3 text-right font-semibold text-red-600">
                                        -$<AnimatedNumber value={totalInitialInvestment} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right">-</td>
                                    <td className="p-3 text-right">-</td>
                                    <td className="p-3 text-right">-</td>
                                    <td className="p-3 text-right">-</td>
                                </tr>
                                {yearByYearData.map((yearData) => (
                                    <tr key={yearData.year} className="border-b border-border">
                                        <td className="p-3 font-semibold">{yearData.year}</td>
                                        <td className="p-3 text-right">
                                            $<AnimatedNumber value={yearData.annualIncome} decimals={2} />
                                        </td>
                                        <td className="p-3 text-right">
                                            $<AnimatedNumber value={yearData.mortgage} decimals={2} />
                                        </td>
                                        <td className="p-3 text-right">
                                            $<AnimatedNumber value={yearData.expenses} decimals={2} />
                                        </td>
                                        <td className={`p-3 text-right font-semibold ${yearData.cashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            $<AnimatedNumber value={yearData.cashFlow} decimals={2} />
                                        </td>
                                        <td className={`p-3 text-right ${yearData.cashOnCashReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            <AnimatedNumber value={yearData.cashOnCashReturn} decimals={2} />%
                                        </td>
                                        <td className="p-3 text-right">
                                            $<AnimatedNumber value={yearData.equityAccumulated} decimals={2} />
                                        </td>
                                        <td className="p-3 text-right">
                                            $<AnimatedNumber value={yearData.cashToReceive} decimals={2} />
                                        </td>
                                        <td className={`p-3 text-right ${yearData.irr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            <AnimatedNumber value={yearData.irr} decimals={2} />%
                                        </td>
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                                    <td className="p-3">Total</td>
                                    <td className="p-3 text-right">
                                        $<AnimatedNumber value={yearByYearData.reduce((sum, d) => sum + d.annualIncome, 0)} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right">
                                        $<AnimatedNumber value={yearByYearData.reduce((sum, d) => sum + d.mortgage, 0)} decimals={2} />
                                    </td>
                                    <td className="p-3 text-right">
                                        $<AnimatedNumber value={yearByYearData.reduce((sum, d) => sum + d.expenses, 0)} decimals={2} />
                                    </td>
                                    <td className={`p-3 text-right ${yearByYearData.reduce((sum, d) => sum + d.cashFlow, 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        $<AnimatedNumber value={yearByYearData.reduce((sum, d) => sum + d.cashFlow, 0)} decimals={2} />
                                    </td>
                                    <td className={`p-3 text-right ${(yearByYearData.reduce((sum, d) => sum + d.cashFlow, 0) / totalInitialInvestment * 100) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        <AnimatedNumber value={totalInitialInvestment > 0 ? (yearByYearData.reduce((sum, d) => sum + d.cashFlow, 0) / totalInitialInvestment * 100) : 0} decimals={2} />%
                                    </td>
                                    <td className="p-3 text-right">-</td>
                                    <td className="p-3 text-right">-</td>
                                    <td className="p-3 text-right">-</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
}

